import { Box, Fade, Typography, useMediaQuery, type SxProps, type Theme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { V } from '../Layout';
import { useShallowMergeSxProps } from '../hooks/useShallowMergeSxProps';
import I18n from '../i18n/I18n';
import HourglassAnimationSlow from '../icons/animations/HourglassAnimationSlow.gif';
import WatchLoaderImage from '../icons/IconImages/WatchLoaderImage';

const DEFAULT_MESSAGE_INTERVAL_MS = 5000;
const DEFAULT_LAST_MESSAGE_INTERVAL_MS = 15000;

export const DEFAULT_WORKFLOW_LOADER_MESSAGE_TOKENS = [
    'workflowLoader.default.settingThingsUp',
    'workflowLoader.default.gettingThingsMoving',
    'workflowLoader.default.preparingDetails',
    'workflowLoader.default.puttingThingsTogether',
    'workflowLoader.default.gettingEverythingInPlace',
    'workflowLoader.default.finishingTouches',
    'workflowLoader.default.almostThere'
];

type WorkflowLoaderProps = {
    /** Direct message content to cycle through. Takes precedence over `messageTokens`. */
    messages?: ReactNode[];
    /** I18n token list to cycle through. Falls back to default workflow messages when omitted. */
    messageTokens?: string[];
    /** Duration for every message except the final one. */
    intervalMs?: number;
    /** Duration for the final message before cycling back to the first message. */
    lastMessageIntervalMs?: number;
    /** Loader illustration shown above the active message. */
    image?: ReactNode;
    /** Minimum vertical space reserved for the loader content. */
    minHeight?: number | string;
    /** Optional styles for the loader container. */
    containerSx?: SxProps<Theme>;
    /** Optional styles for the active message. */
    messageSx?: SxProps<Theme>;
};

const DEFAULT_WORKFLOW_LOADER_CONTAINER_SX: SxProps<Theme> = {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center'
};

const DEFAULT_WORKFLOW_LOADER_STATIC_IMAGE_SX: SxProps<Theme> = {
    '& svg': { width: 280, height: 'auto' }
};

const workflowLoaderMessageColor = keyframes`
    0% {
        animation-timing-function: ease-in-out;
        color: #000000;
    }
    10% {
        animation-timing-function: ease-in-out;
        color: #056BD9;
    }
    16.667% {
        animation-timing-function: ease-in-out;
        color: #05B2E0;
    }
    33.333% {
        animation-timing-function: ease-in-out;
        color: #000000;
    }
    43.333% {
        animation-timing-function: ease-in-out;
        color: #056BD9;
    }
    50% {
        animation-timing-function: ease-in-out;
        color: #05B2E0;
    }
    66.667% {
        animation-timing-function: ease-in-out;
        color: #000000;
    }
    76.667% {
        animation-timing-function: ease-in-out;
        color: #056BD9;
    }
    83.333% {
        animation-timing-function: ease-in-out;
        color: #05B2E0;
    }
    100% {
        color: #000000;
    }
`;

const DEFAULT_WORKFLOW_LOADER_MESSAGE_SX: SxProps<Theme> = {
    color: '#000000',
    fontWeight: 600,
    animation: `${workflowLoaderMessageColor} 9s linear infinite`,
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none'
    }
};

const DefaultWorkflowLoaderImage = () => {
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    if (prefersReducedMotion) {
        return (
            <Box aria-hidden="true" sx={DEFAULT_WORKFLOW_LOADER_STATIC_IMAGE_SX}>
                <WatchLoaderImage />
            </Box>
        );
    }

    return <Box component="img" src={HourglassAnimationSlow} alt="" width={280} height="auto" />;
};

const DEFAULT_WORKFLOW_LOADER_IMAGE = <DefaultWorkflowLoaderImage />;

export default function WorkflowLoader({
    messages,
    messageTokens,
    intervalMs = DEFAULT_MESSAGE_INTERVAL_MS,
    lastMessageIntervalMs = DEFAULT_LAST_MESSAGE_INTERVAL_MS,
    image = DEFAULT_WORKFLOW_LOADER_IMAGE,
    minHeight = 420,
    containerSx,
    messageSx
}: WorkflowLoaderProps) {
    const resolvedMessages = useMemo(() => {
        if (messages?.length) {
            return messages;
        }

        const tokens = messageTokens?.length ? messageTokens : DEFAULT_WORKFLOW_LOADER_MESSAGE_TOKENS;

        return tokens.map((token) => <I18n key={token} token={token} />);
    }, [messages, messageTokens]);
    const [messageIndex, setMessageIndex] = useState(0);
    const containerStyles = useShallowMergeSxProps(DEFAULT_WORKFLOW_LOADER_CONTAINER_SX, { minHeight });
    const mergedContainerStyles = useShallowMergeSxProps(containerStyles, containerSx);
    const mergedMessageStyles = useShallowMergeSxProps(DEFAULT_WORKFLOW_LOADER_MESSAGE_SX, messageSx);

    useEffect(() => {
        setMessageIndex(0);
    }, [resolvedMessages]);

    useEffect(() => {
        if (resolvedMessages.length <= 1) {
            return undefined;
        }

        const isLastMessage = messageIndex === resolvedMessages.length - 1;
        const timeoutId = setTimeout(
            () => setMessageIndex((currentIndex) => (currentIndex + 1) % resolvedMessages.length),
            isLastMessage ? lastMessageIntervalMs : intervalMs
        );

        return () => clearTimeout(timeoutId);
    }, [intervalMs, lastMessageIntervalMs, messageIndex, resolvedMessages.length]);

    return (
        <V fill sx={mergedContainerStyles}>
            {image}
            <Fade key={messageIndex} in timeout={250}>
                <Typography role="status" aria-live="polite" sx={mergedMessageStyles}>
                    {resolvedMessages[messageIndex]}
                </Typography>
            </Fade>
        </V>
    );
}
